import { Stack, Text } from "@chakra-ui/react";
import { SimpleList } from "@/components/list";

export const FAQS = [
  {
    title: "What factors are considered when designing a network architecture?",
    content: (
      <Stack>
        <Text>A well-designed network architecture considers: </Text>
        <SimpleList
          points={[
            "Business needs and scalability requirements.",
            "Security measures such as zero-trust frameworks.",
            "Integration with cloud services and remote access capabilities.",
            "Traffic flow optimization for performance efficiency.",
          ]}
        />
      </Stack>
    ),
  },
  {
    title: "What is the difference between network design and network deployment?",
    content: (
      <SimpleList
        points={[
          "Network design involves strategic planning, blueprint creation, and security structuring to build a future-proof infrastructure.",
          "Network deployment is the execution phase, involving the installation of hardware, software, and configurations for a fully functional network.",
        ]}
      />
    ),
  },
  {
    title: "How does OneMind Cloud Services ensure network security during deployment?",
    content: (
      <Stack>
        <Text>We implement multi-layered security strategies, including: </Text>
        <SimpleList
          points={[
            "End-to-end encryption for data protection.",
            "Network segmentation to isolate sensitive areas.",
            "Continuous vulnerability assessments to detect and mitigate threats.",
          ]}
        />
      </Stack>
    ),
  },
  {
    title: "How do cloud-based network deployments improve business operations?",
    content: (
      <Stack>
        <Text>Cloud-based deployments provide: </Text>
        <SimpleList
          points={[
            "Scalability – Easily expand network capacity based on demand.",
            "Remote management – Centralized control via cloud dashboards.",
            "Cost efficiency – Reduced hardware dependence and maintenance expenses.",
          ]}
        />
      </Stack>
    ),
  },
  {
    title:
      "What industries benefit the most from professional network design and deployment?",
    content: (
      <SimpleList
        points={[
          "Healthcare – Ensuring data transmission.",
          "Finance – Secure, high-speed transactions with regulatory compliance.",
          "Retail & E-commerce – Omnichannel networking for seamless customer experiences.",
          "Manufacturing – Industrial IoT (IIoT) connectivity for smart factories.",
        ]}
      />
    ),
  },
  {
    title: "What is the role of automation in network design and deployment?",
    content: (
      <SimpleList
        points={[
          "AI-driven automation simplifies configuration, monitoring, and issue resolution.",
          "Automated deployment scripts speed up implementation.",
          "Self-healing networks enhance reliability and minimize downtime.",
        ]}
      />
    ),
  },
  {
    title:
      "How can businesses upgrade their existing network without disrupting operations?",
    content: (
      <SimpleList
        points={[
          "Perform phased migrations to minimize downtime.",
          "Use redundant network paths to prevent service interruptions.",
          "Conduct real-time performance monitoring during upgrades.",
        ]}
      />
    ),
  },
  {
    title: "What are the best practices for designing a high-availability network?",
    content: (
      <Stack>
        <Text>To ensure high availability, a network should include: </Text>
        <SimpleList
          points={[
            "Redundant connections to prevent single points of failure.",
            "Load balancing to distribute traffic efficiently.",
            "Automated failover mechanisms to maintain uptime during outages.",
            "Proactive network monitoring to detect and resolve issues before they escalate.",
          ]}
        />
      </Stack>
    ),
  },
  {
    title: "How does SD-WAN improve network deployment for multi-site businesses?",
    content: (
      <Stack>
        <Text>SD-WAN provides: </Text>
        <SimpleList
          points={[
            "Centralized cloud management for easy deployment across multiple locations.",
            "Optimized bandwidth usage by prioritizing critical applications.",
            "Secure direct internet access without backhauling traffic to a central data center.",
            "Automated traffic routing for improved performance and reduced latency.",
          ]}
        />
      </Stack>
    ),
  },
  {
    title:
      "What challenges do businesses face when transitioning from legacy networks to modern infrastructure?",
    content: (
      <Stack>
        <Text>Key challenges include: </Text>
        <SimpleList
          points={[
            "Compatibility issues with existing hardware and applications.",
            "Security risks during the migration process.",
            "Downtime concerns affecting business operations.",
            "Training employees to adapt to new technologies.",
          ]}
        />
        <Text>
          OneMind Cloud Services ensures a smooth transition by using phased migrations
          and hybrid deployment strategies.{" "}
        </Text>
      </Stack>
    ),
  },
  {
    title: "How does cloud-based network design reduce costs for businesses?",
    content: (
      <Stack>
        <Text>Cloud-based networks help businesses: </Text>
        <SimpleList
          points={[
            "Eliminate the need for physical infrastructure, reducing CapEx.",
            "Scale resources dynamically, paying only for what’s needed.",
            "Reduce IT maintenance costs with automated updates and remote management.",
            "Improve workforce mobility, enabling secure remote work solutions.",
          ]}
        />
      </Stack>
    ),
  },
  {
    title: "What is the importance of a site survey before network deployment?",
    content: (
      <Stack>
        <Text>A site survey helps: </Text>
        <SimpleList
          points={[
            "Identify coverage gaps for wired and wireless infrastructure.",
            "Determine bandwidth requirements based on user traffic.",
            "Assess potential interference sources (physical obstructions, competing networks).",
            "Optimize access point placement for maximum signal strength.",
          ]}
        />
        <Text>
          OneMind Cloud Services conducts detailed site assessments to ensure optimal
          network performance.{" "}
        </Text>
      </Stack>
    ),
  },
  {
    title: "How does network segmentation improve security and performance?",
    content: (
      <Stack>
        <Text>Network segmentation: </Text>
        <SimpleList
          points={[
            "Isolate critical systems to prevent unauthorized access.",
            "Reduces congestion by managing traffic more efficiently.",
            "Enhances security by limiting the spread of cyber threats.",
            "Enables better compliance with regulatory standards (e.g., GDPR).",
          ]}
        />
        <Text>
          OneMind Cloud Services designs segmented networks to maximize security and
          performance.
        </Text>
      </Stack>
    ),
  },
  {
    title: "What tools and technologies are used in modern network deployment?",
    content: (
      <Stack>
        <Text>Modern network deployments utilize: </Text>
        <SimpleList
          points={[
            "AI-driven network monitoring for proactive issue detection.",
            "Cloud-based controllers for centralized network management.",
            "SDN (Software-Defined Networking) for flexible and programmable networks.",
            "Zero-trust security models to enhance data protection.",
          ]}
        />
      </Stack>
    ),
  },
];
